import React from "react";
import PropTypes from "prop-types";
import { ArrowRightIcon } from "../../../design-system";
import { borders, surfaces, textColors, typography } from "../../../design-system/tokens";
import { formatCount } from "./automationUtils";

/**
 * The automation funnel — "situation matched → entered → guards passed →
 * policy suppressed → enqueued" — rendered either as a one-line compact
 * strip (table cells) or as labelled bars (preview panel, runs drawer).
 *
 * `mode` only changes the wording: a preview says "would enter / would
 * enqueue", a recorded run says "entered / enqueued".
 */

const buildSteps = (counts, mode) => {
  const preview = mode === "preview";
  return [
    {
      key: "matched",
      label: "Matched",
      description: "Users in the situation right now.",
      value: counts.matched,
      tone: "neutral",
    },
    {
      key: "entered",
      label: preview ? "Would enter" : "Entered",
      description: "New entries — users who were not already inside the situation. Only they can receive the messages.",
      value: counts.entered,
      tone: "neutral",
    },
    {
      key: "guards",
      label: "Guards passed",
      description: "Entries that also pass every guard.",
      value: counts.guards,
      tone: "neutral",
    },
    {
      key: "suppressed",
      label: "Policy suppressed",
      description: "Held back by the delivery policy (frequency caps, suppressed addresses, marketing opt-out).",
      value: counts.suppressed,
      tone: "warning",
    },
    {
      key: "enqueued",
      label: preview ? "Would enqueue" : "Enqueued",
      description: preview ? "Users who would receive the messages if this ran now." : "Users whose messages were enqueued.",
      value: counts.enqueued,
      tone: "success",
    },
  ];
};

const normalizeCounts = (counts) => ({
  matched: Number(counts?.matched ?? 0),
  entered: Number(counts?.entered ?? 0),
  guards: Number(counts?.guards ?? 0),
  suppressed: Number(counts?.suppressed ?? 0),
  enqueued: Number(counts?.enqueued ?? 0),
});

const toneBarClasses = {
  neutral: "bg-blue-500 dark:bg-blue-400",
  warning: "bg-yellow-500 dark:bg-yellow-400",
  success: "bg-green-500 dark:bg-green-400",
};

const toneTextClasses = {
  neutral: textColors.primary,
  warning: "text-yellow-700 dark:text-yellow-300",
  success: "text-green-700 dark:text-green-300",
};

const RunFunnel = ({ counts, mode = "run", variant = "full", className = "" }) => {
  const normalized = normalizeCounts(counts);
  const steps = buildSteps(normalized, mode);

  if (variant === "compact") {
    // Inline "matched → entered → guards → enqueued" (suppressed is shown as a badge by the caller).
    const inline = steps.filter((s) => s.key !== "suppressed");
    const title = inline.map((s) => `${s.label}: ${formatCount(s.value)}`).join(" → ");
    return (
      <span
        className={`inline-flex items-center gap-1 whitespace-nowrap ${typography.bodySmall} ${className}`}
        title={title}
        aria-label={title}
      >
        {inline.map((step, index) => (
          <React.Fragment key={step.key}>
            {index > 0 && (
              <ArrowRightIcon size="xs" className={`${textColors.muted} shrink-0`} aria-hidden="true" />
            )}
            <span className={`${toneTextClasses[step.tone]} font-medium tabular-nums`}>{formatCount(step.value)}</span>
          </React.Fragment>
        ))}
      </span>
    );
  }

  const max = Math.max(1, ...steps.map((s) => s.value));
  return (
    <ol className={`space-y-2 ${className}`} aria-label="Automation funnel">
      {steps.map((step) => {
        const width = Math.max(step.value > 0 ? 4 : 0, Math.round((step.value / max) * 100));
        return (
          <li key={step.key} className="min-w-0">
            <div className="flex items-baseline justify-between gap-3">
              <span className={`${typography.bodySmall} ${textColors.secondary} truncate`} title={step.description}>
                {step.label}
              </span>
              <span className={`${typography.bodySmall} font-semibold tabular-nums ${toneTextClasses[step.tone]}`}>
                {formatCount(step.value)}
              </span>
            </div>
            <div
              className={`mt-1 h-1.5 w-full rounded-full overflow-hidden ${surfaces.tertiary} border ${borders.light}`}
              role="presentation"
            >
              <div
                className={`h-full rounded-full transition-[width] duration-300 ${toneBarClasses[step.tone]}`}
                style={{ width: `${width}%` }}
              />
            </div>
          </li>
        );
      })}
    </ol>
  );
};

RunFunnel.propTypes = {
  counts: PropTypes.shape({
    matched: PropTypes.number,
    entered: PropTypes.number,
    guards: PropTypes.number,
    suppressed: PropTypes.number,
    enqueued: PropTypes.number,
  }),
  mode: PropTypes.oneOf(["preview", "run"]),
  variant: PropTypes.oneOf(["compact", "full"]),
  className: PropTypes.string,
};

/** Funnel counts from an AutomationRun payload (list `last_run`, drawer rows). */
export const runToFunnelCounts = (run) => ({
  matched: run?.situation_matched_count ?? 0,
  entered: run?.entered_count ?? 0,
  guards: run?.guards_passed_count ?? 0,
  suppressed: run?.policy_suppressed_count ?? 0,
  enqueued: run?.enqueued_count ?? 0,
});

/** Funnel counts from a preview response. */
export const previewToFunnelCounts = (preview) => ({
  matched: preview?.situation_matched_count ?? 0,
  entered: preview?.would_enter_count ?? 0,
  guards: preview?.guards_passed_count ?? 0,
  suppressed: preview?.policy_suppressed_count ?? 0,
  enqueued: preview?.would_enqueue_count ?? 0,
});

export default RunFunnel;
